import * as React from 'react';
import { Box, Grid, CircularProgress, Alert } from '@mui/material';
import { useDashboardData } from './hooks/useDashboardData';
import StatsCard from './components/StatsCard';
import DashboardChart from './components/DashboardChart';
import DashboardList from './components/DashboardList';
import imagePaths from '../conf/imageConfig';

const Dashboard: React.FC = () => {
  const {
    stats,
    incomeData,
    ordersData,
    pendingItems,
    activeOrders,
    recentTopups,
    loading,
    error,
  } = useDashboardData();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Alert severity="error" sx={{ width: { xs: '90%', sm: '50%' } }}>
          {error}
        </Alert>
      </Box>
    );
  }

  const statsConfig = [
    {
      title: 'Monthly Income',
      value: stats.monthlyIncome,
      icon: imagePaths.income,
      alt: 'Monthly Income',
    },
    {
      title: 'New Orders',
      value: stats.newOrders,
      icon: imagePaths.orders,
      alt: 'Orders',
    },
    {
      title: 'Order Active',
      value: stats.activeOrders,
      icon: imagePaths.orderActive,
      alt: 'Order active',
    },
    {
      title: 'New Users',
      value: stats.newUsers,
      icon: imagePaths.user,
      alt: 'New Users',
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 8 } }}>
      {/* Stats Cards Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statsConfig.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              alt={stat.alt}
            />
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <DashboardChart
            title="Income this month"
            data={incomeData}
            dataKey="income"
            gradientId="incomeGradient"
            strokeColor="#a16bfe"
            formatValue={(value: number) => (value === 0 ? '0' : `${value / 1000}k`)}
            yAxisDomain={[0, 200000]}
          />
        </Grid>
        <Grid item xs={12}>
          <DashboardChart
            title="Orders this month"
            data={ordersData}
            dataKey="orders"
            gradientId="ordersGradient"
            strokeColor="#8884d8"
          />
        </Grid>
      </Grid>

      {/* Lists Section */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <DashboardList
            title="Pending Verification Approval"
            items={pendingItems}
            emptyMessage="No pending verifications"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DashboardList
            title="Active Orders"
            items={activeOrders}
            emptyMessage="No active orders"
            showDescription
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <DashboardList
            title="Recent Topups"
            items={recentTopups}
            emptyMessage="No recent topups"
            showDescription
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;